import { useEffect, useState } from "react";
import { Link2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/KpiCard";
import { PetitSelect } from "@/components/OperationBlock";
import { SmartTable } from "@/components/SmartTable";
import { IMMEUBLE } from "@/components/LogementItems";
import { api, errMsg, money } from "@/lib/api";

const libelle = (l) => (!l ? "—" : l === "__commun__" ? "Commun" : l.startsWith(IMMEUBLE) ? `Immeuble entier — ${l.slice(IMMEUBLE.length)}` : l);

const Ligne = ({ f, logements, onDone }) => {
  const [cible, setCible] = useState(f.logement || "");
  useEffect(() => { setCible(f.logement || ""); }, [f.logement]);
  const [busy, setBusy] = useState(false);
  const rattacher = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/fournisseurs/rattacher", { cle: f.cle, logement: cible });
      toast.success(`${data.modifiees} opération(s) de « ${f.cle} » rattachée(s) à ${libelle(cible)} et mémorisée(s)`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <tr className="border-b border-slate-100" data-testid={`fournisseur-${f.cle}`}>
      <td className="py-2 px-2"><p className="font-mono text-xs">{f.cle}</p><p className="text-[11px] text-slate-400 truncate max-w-xs" title={f.exemple}>{f.exemple}</p></td>
      <td className="py-2 px-2 text-right font-mono text-xs">{f.nb}</td>
      <td className="py-2 px-2 text-right font-mono text-xs text-red-700">-{money(f.total)}</td>
      <td className="py-2 px-2 font-mono text-xs text-slate-500">{f.dernier}</td>
      <td className="py-2 px-2 text-xs" data-testid={`fournisseur-actuel-${f.cle}`}>{libelle(f.logement)}<span className="block text-[10px] text-slate-400">{f.rattachees}/{f.nb} rattachée(s)</span></td>
      <td className="py-2 px-2 w-64"><PetitSelect parImmeuble avecImmeuble value={cible} options={logements} placeholder="Choisir l'immeuble…" onChange={setCible} testId={`fournisseur-cible-${f.cle}`} /></td>
      <td className="py-2 px-2"><Button size="sm" disabled={!cible || busy} onClick={rattacher} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`fournisseur-rattacher-${f.cle}`}>
        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Link2 className="h-4 w-4 mr-1" />}Rattacher ({f.nb})</Button></td>
    </tr>
  );
};

export const FournisseursPanel = ({ logements, version, onChange }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/fournisseurs").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, [version]);
  const visibles = (rows || []).filter((f) => `${f.cle} ${f.exemple}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Panel title="Rattacher par fournisseur" testId="fournisseurs-panel" action={
      <div className="relative w-56"><Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hydro, assurance, ville…" className="h-9 pl-8 bg-white" data-testid="fournisseurs-recherche" /></div>}>
      <p className="text-xs text-slate-500 mb-3">Retraits regroupés par fournisseur (même libellé bancaire, toutes périodes). « Rattacher » attribue toutes ses opérations à l'immeuble entier ou au logement choisi et le mémorise pour les prochaines : le résumé par immeuble se remplit d'un coup. Une charge « Immeuble entier » est répartie entre ses logements dans le bilan.</p>
      {!rows ? <Loader2 className="h-5 w-5 animate-spin text-emerald-700" /> : visibles.length === 0 ? <p className="text-sm text-slate-500" data-testid="fournisseurs-vide">Aucun fournisseur.</p> : (
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <SmartTable id="fournisseurs" className="w-full text-sm" data-testid="fournisseurs-table">
            <thead className="sticky top-0 bg-white"><tr className="border-b border-slate-200 text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
              <th className="py-2 px-2">Fournisseur</th><th className="py-2 px-2 text-right">Opérations</th><th className="py-2 px-2 text-right">Total</th>
              <th className="py-2 px-2">Dernier</th><th className="py-2 px-2">Rattaché à</th><th className="py-2 px-2">Rattacher à</th><th className="py-2 px-2" />
            </tr></thead>
            <tbody>{visibles.map((f) => <Ligne key={f.cle} f={f} logements={logements} onDone={onChange} />)}</tbody>
          </SmartTable>
        </div>
      )}
    </Panel>
  );
};
